import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { action, observable, reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import Modal from 'react-modal';

import {
	AddressModal,
	ConfirmPromptModal,
	ConfirmUnvalidatedDropshipModal,
	ConfirmMultipleEntryDropshipModal,
} from 'ui/components';
import { FormattedMessage, getMessagesForLocale } from 'ui/i18n';

import Arrow from 'ui/assets/images/icons/table_dropdown_icon.svg';
import { MAX_DROPSHIP_CHANGES_SAME_ITEM } from 'config/constants';
import './DropShipAddress.scss';

function containsRange(address) {
	return /(^| )\d+-\d+($| )/.test(address);
}

@inject('AppState', 'CartState', 'ConfirmPromptState', 'CartItemsState')
@observer
export default class DropShipAddress extends Component {
	static propTypes = {
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		AppState: PropTypes.object.isRequired,
		ConfirmPromptState: PropTypes.object.isRequired,
		item: PropTypes.object.isRequired,
	};

	componentWillMount() {
		reaction(
			() => this.props.CartState.addDropShipAddress.state.resolved,
			result => {
				if (result) {
					if (!this.props.CartState.errorId) {
						this.props.CartItemsState.emptyCurrentCart();
						this.sync();
					}
				}
			}
		);

		reaction(
			() => this.props.CartState.updateDropShipAddress.state.resolved,
			result => {
				if (result) {
					if (!this.props.CartState.errorId) {
						this.props.CartItemsState.emptyCurrentCart();
						this.sync();
					}
				}
			}
		);

		reaction(
			() => this.props.CartState.validateDropShipAddress.state.resolved,
			result => {
				if (
					result &&
					this.props.CartState.validatedItem === this.props.item &&
					!this.props.CartState.errorId
				) {
					if (
						this.props.CartState.hasOneEntryDropship &&
						this.isSingleCandidateValid()
					) {
						this.processDropship();
					} else {
						this.toggleDropshipModal();
					}
				}
			}
		);
	}

	componentDidMount() {
		document.addEventListener('click', this.handleDocumentClick.bind(this));
	}

	@action
	componentWillReceiveProps(props) {
		if (!this.showAddressModal && !props.item.drop_ship_address) {
			this.current = 'None';
		} else if (props.item.drop_ship_address && !this.showAddressModal) {
			this.current = props.item.drop_ship_address.name;
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleDocumentClick.bind(this));
	}

	mouseWasDown = false;
	mouseWasUp = false;
	@observable
	current = this.props.item.drop_ship_address
		? this.props.item.drop_ship_address.name
		: 'None';
	@observable drop_ships = this.props.CartState.cart.drop_ships;
	@observable showAddressModal = false;
	@observable inEditMode = false;
	@observable show = false;
	@observable togglePrompt = false;
	@observable showDropShip = false;
	@observable cachedNewAddress = null;
	@observable
	address = {
		name: '',
		technician: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		stateAbbreviation: '',
		country: '',
		zip: '',
		phone: '',
		email: '',
	};

	@action
	toggleDropshipModal() {
		this.showDropShip = !this.showDropShip;
	}

	@action
	goBackToEdit() {
		this.showAddressModal = true;
		this.showDropShip = false;

		const isEditMode = this.props.CartState.cart.drop_ships.some(
			dropship => dropship.name === this.cachedNewAddress.name
		);

		if (isEditMode) {
			this.current = 'Edit Address';
			this.inEditMode = true;
		} else {
			this.current = 'New Address';
			this.inEditMode = false;
		}
	}

	@action
	toggleCloseDropshipModal() {
		this.showDropShip = !this.showDropShip;
		this.processDropship();
	}

	isSingleCandidateValid() {
		const address = this.cachedNewAddress.address1;
		const candidateAddress = this.props.CartState.selectCandidateId.address1;

		if (!containsRange(address) && containsRange(candidateAddress)) {
			return false;
		}

		return true;
	}

	processDropship() {
		const candidate = this.props.CartState.selectCandidateId;
		const dropshipUps = this.mapCandidateToDropshipModel(candidate);

		this.addOrUpdateDropship(dropshipUps);
		this.clearCacheNewAddress();
	}

	mapCandidateToDropshipModel(candidate) {
		return (this.address = {
			address1: candidate.address1 || '',
			address2: candidate.address2 || '',
			stateAbbreviation: candidate.politicalDivision1 || '',
			state: this.props.CartState.handleState(
				candidate.countryCode,
				candidate.politicalDivision1
			),
			country: candidate.countryCode || '',
			zip: candidate.postcodePrimaryLow || '',
			name: this.cachedNewAddress.name,
			technician: this.cachedNewAddress.technician,
			phone: this.cachedNewAddress.phone,
			email: this.cachedNewAddress.email,
			city: candidate.politicalDivision2,
		});
	}

	@action
	toggleCloseUnvalidatedDropshipModal() {
		this.showDropShip = !this.showDropShip;
		this.addOrUpdateDropship(null);

		this.clearCacheNewAddress();
	}

	addOrUpdateDropship(dropship) {
		const dropshipRequest = dropship || this.cachedNewAddress;
		if (this.cachedNewAddress.name === this.current) {
			this.props.CartState.updateDropShipAddress(
				dropshipRequest,
				this.props.item
			);
		} else {
			this.props.CartState.addDropShipAddress(dropshipRequest, this.props.item);
		}
	}

	@action.bound
	clearCacheNewAddress() {
		this.cachedNewAddress = null;
	}

	@action.bound
	saveCacheAddress(address) {
		this.cachedNewAddress = address;
	}

	isSelectedOption(option) {
		return this.current === option;
	}

	handleMouseDown() {
		this.mouseWasDown = true;
	}

	handleMouseUp() {
		this.mouseWasUp = true;
	}

	buildAddressToDisplay() {
		if (this.props.item.drop_ship_address) {
			return (
				<div className="DropShipAddress-address">
					<div className="break-all">
						{this.props.item.drop_ship_address.technician}
					</div>
					<div className="break-all">
						{this.props.item.drop_ship_address.address1}
					</div>
					{this.props.item.drop_ship_address.address2
						? <div className="break-all">
								{this.props.item.drop_ship_address.address2}
							</div>
						: ''}
					<div className="break-normal">
						{this.props.item.drop_ship_address.city},{' '}
						{this.props.item.drop_ship_address.stateAbbreviation
							? `${this.props.item.drop_ship_address.stateAbbreviation}, `
							: ''}
						{this.props.item.drop_ship_address.zip},{' '}
						{this.props.item.drop_ship_address.country}
					</div>
					<div>
						{this.props.item.drop_ship_address.phone}
					</div>
					<div className="break-all">
						{this.props.item.drop_ship_address.email}
					</div>
				</div>
			);
		}

		return '';
	}

	@action
	togglePromptModal() {
		this.togglePrompt = !this.togglePrompt;
	}

	@action
	handlePromptClosing(choice) {
		if (choice === 'cancel') {
			this.props.CartItemsState.emptyCurrentCart();
			this.props.CartState.deleteAllUpdates();
			this.toggleModal(this.current);
			this.togglePromptModal();
		} else {
			this.toggleModal(this.current);
			this.current = this.props.item.drop_ship_address
				? this.props.item.drop_ship_address.name
				: 'None';
		}
	}

	@action
	sync() {
		if (this.props.item.drop_ship_address) {
			this.current = this.props.item.drop_ship_address.name;
		} else {
			this.current = 'None';
		}

		this.drop_ships = this.props.CartState.cart.drop_ships;
	}

	@action
	handleDocumentClick() {
		if (!this.mouseWasDown && !this.mouseWasUp && this.show) {
			this.show = false;
		}

		this.mouseWasDown = false;
		this.mouseWasUp = false;
	}

	@action
	onRequestClose() {
		this.toggleModal(this.current);

		this.current = this.props.item.drop_ship_address
			? this.props.item.drop_ship_address.name
			: 'None';
	}

	@action
	onRequestNotificationClose() {
		this.toggleDropshipNotification();

		this.current = this.props.item.initial_drop_ship_address
			? this.props.item.initial_drop_ship_address.name
			: 'None';

		this.props.item.drop_ship_address = this.props.item.initial_drop_ship_address;
	}

	@action
	toggleModal(value) {
		switch (value) {
			case 'New Address':
				if (
					!this.showAddressModal &&
					this.props.CartItemsState.cartHasBeenUpdated
				) {
					this.togglePromptModal();
				} else {
					this.showAddressModal = !this.showAddressModal;
				}
				break;
			case 'Edit Address':
				if (
					!this.showAddressModal &&
					this.props.CartItemsState.cartHasBeenUpdated
				) {
					this.togglePromptModal();
				} else {
					this.showAddressModal = !this.showAddressModal;
					this.inEditMode = !this.inEditMode;
				}
				break;
		}
	}

	@action
	onClickHandler(option, data) {
		this.current = option;

		if (data) {
			this.props.item.drop_ship_address = data;
		} else if (option) {
			this.props.item.drop_ship_address = null;
		}

		this.toggleModal(option);

		if (option !== 'New Address' && option !== 'Edit Address') {
			const dropship = this.props.item.drop_ship_address;
			const initialDropship = this.props.item.current_drop_ship_address;

			if (
				(dropship === null && initialDropship === null) ||
				(dropship !== null &&
					initialDropship !== null &&
					dropship.name === initialDropship.name)
			) {
				this.props.CartItemsState.deleteUpdate(
					this.props.item,
					'drop_ship_address'
				);
			} else if (
				this.props.item.is_drop_ship &&
				this.props.CartItemsState.getNumberOfDropshipChanges(this.props.item) >=
					MAX_DROPSHIP_CHANGES_SAME_ITEM
			) {
				this.toggleDropshipNotification();
			} else {
				this.props.CartItemsState.addUpdate(
					this.props.item,
					data,
					'drop_ship_address'
				);
			}
		}
	}

	@action
	toggle() {
		this.show = !this.show;
	}

	@action
	toggleDropshipNotification() {
		this.showDropShipNotification = !this.showDropShipNotification;
	}

	buildOptions() {
		const options = [
			{ value: 'None', show: true, data: null },
			{
				value: 'New Address',
				show: true,
				data: this.props.item.drop_ship_address,
			},
			{
				value: 'Edit Address',
				show: !!this.props.item.drop_ship_address,
				data: this.props.item.drop_ship_address,
			},
		];

		if (this.drop_ships) {
			this.drop_ships.map(drop_ship =>
				options.push({ value: drop_ship.name, show: true, data: drop_ship })
			);
		}

		return options;
	}

	renderDropdownOption(option) {
		switch (option) {
			case 'None':
			case 'Aucun':
				return <FormattedMessage id="dropship.optionNone" />;
			case 'New Address':
			case 'Nouvelle adresse':
				return <FormattedMessage id="dropship.optionNewAddress" />;
			case 'Edit Address':
			case 'Modifier une adresse':
				return <FormattedMessage id="dropship.optionEditAddress" />;
			default:
				return (
					<span>
						{option}
					</span>
				);
		}
	}

	render() {
		const options = this.buildOptions();
		const { locale } = this.props.AppState;

		return (
			<div className="DropShipAddress">
				<div
					className="DropShipAddress-container"
					onClick={this.toggle.bind(this)}
					onMouseDown={this.handleMouseDown.bind(this)}
					onMouseUp={this.handleMouseUp.bind(this)}
				>
					{this.renderDropdownOption(this.current)}
					<span className="arrow-icon" />
					{this.show &&
						<div className="DropShipAddress-container-content">
							<span className="content-arrow">
								<img src={Arrow} />
							</span>
							{options.map(
								(option, index) =>
									option.show
										? <div
												key={index}
												className={`content-control ${this.isSelectedOption(
													option.value
												)
													? 'selected'
													: ''}`}
												onClick={this.onClickHandler.bind(
													this,
													option.value,
													option.data
												)}
											>
												{this.renderDropdownOption(option.value)}
											</div>
										: ''
							)}
						</div>}
				</div>

				{this.buildAddressToDisplay()}

				{this.togglePrompt &&
					<ConfirmPromptModal
						msg={getMessagesForLocale(locale)['cart.leaveCartMessage']}
						useState={false}
						show={this.togglePrompt}
						onClick={this.handlePromptClosing.bind(this)}
					/>}

				{this.showAddressModal &&
					<AddressModal
						cacheNewAddress={this.cachedNewAddress}
						saveCacheAddress={this.saveCacheAddress}
						show={this.showAddressModal}
						item={this.props.item}
						onRequestClose={this.onRequestClose.bind(this)}
						inEditMode={this.inEditMode}
						clearCacheNewAddress={this.clearCacheNewAddress}
					/>}

				{this.showDropShipNotification &&
					<Modal
						className="ReactModal__Content EmptyCartModal"
						overlayClassName="ReactModal__Overlay"
						isOpen={this.showDropShipNotification}
						onRequestClose={this.onRequestNotificationClose.bind(this)}
						contentLabel="Dropship Notification"
					>
						<div className="EmptyCartModal-warning-msg">
							<h4>
								<strong>
									<FormattedMessage id="dropship.NotificationTitle" />
								</strong>
							</h4>
							<hr />
							<h5 id="DropShipMaxNotification">
								<FormattedMessage id="dropship.Notification" />
							</h5>
							<button
								className="btn btn-primary pull-right"
								onClick={this.onRequestNotificationClose.bind(this)}
							>
								<FormattedMessage id="ok.Button" />
							</button>
						</div>
					</Modal>}

				{this.showDropShip &&
					(this.props.CartState.hasMultipleEntryDropships
						? <ConfirmMultipleEntryDropshipModal
								show={this.showDropShip}
								onRequestClose={this.goBackToEdit.bind(this)}
								onRequestConfirm={this.toggleCloseDropshipModal.bind(this)}
								dropshipUps={this.props.CartState.dropshipUps}
							/>
						: <ConfirmUnvalidatedDropshipModal
								show={this.showDropShip}
								onEdit={this.goBackToEdit.bind(this)}
								onRequestConfirm={this.toggleCloseUnvalidatedDropshipModal.bind(
									this
								)}
							/>)}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/components/DropShipAddress/index.js